
import { computed, defineComponent, onBeforeMount, reactive, ref, Ref } from 'vue'
import { useRoute } from 'vue-router'

// @ui-kit
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'

// @components
// import NotificationsBadge from '@/modules/NotificationsModule/components/NotificationsBadge/index.vue'
import HeaderSearchSuggestions from '@/modules/NewDispatcher/components/HeaderSearchSuggestions/index.vue'
import HeaderProfile from '@/modules/NewDispatcher/components/HeaderProfile/index.vue'
import HeaderProfileDropdown from '@/modules/NewDispatcher/components/HeaderProfileDropdown/index.vue'

import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import { GlobalSearchT } from '@/core/types/global_search.types'

// @realisations
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'
import { global_search } from '@/core/realisations/global_search.realisation'

// @composable
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'
import { useOnOutsidePress, useDebounce } from 'vue-composable'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    props: {
        withoutSectors: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        NewInput,
        NewSelect,
        // NotificationsBadge,
        HeaderSearchSuggestions,
        HeaderProfile,
        HeaderProfileDropdown,
        NewButton,
    },
    setup(props) {
        const route = useRoute()
        const new_dispatcher_store = useNewDispatcherStore()
        const notifications = useNotifications()

        const state = reactive({
            is_header_dropdown_opened: false,
            is_custom_checked: false,
            withoutSectorId: null as number | null,
        })

        const elementref: Ref<HTMLElement | null> = ref(null)
        const searchref: Ref<HTMLElement | null> = ref(null)

        const search_state = reactive({
            is_loading: false,
            is_failed: false,
            query: null as string | null,
            suggestions: [] as GlobalSearchT[],
        })

        const data_state = reactive({
            dispatcher_sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as DispatcherSector[],
            },
        })

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcher_sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getAllDispatcherSectors()
                data_state.dispatcher_sectors.list = response.data.data

                data_state.dispatcher_sectors.is_failed = false
            } catch (error) {
                data_state.dispatcher_sectors.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении секторов диспетчера произошла ошибка',
                })
            } finally {
                data_state.dispatcher_sectors.is_loading = false
            }
        }

        const toggleHeaderDropdown = () => {
            state.is_header_dropdown_opened = !state.is_header_dropdown_opened
        }

        const closeHeaderDropdown = () => {
            state.is_header_dropdown_opened = false
        }

        const search = async () => {
            if (search_state.is_loading) return

            if (!search_state.query) {
                search_state.is_failed = false
                search_state.suggestions = []
                return
            }

            try {
                search_state.is_loading = true

                const response = await global_search.search(search_state.query)
                search_state.suggestions = response.data

                search_state.is_failed = false
            } catch (error) {
                search_state.is_failed = true
                console.error(error)
            } finally {
                search_state.is_loading = false
            }
        }

        const resetSearchState = () => {
            search_state.query = null
            search_state.suggestions = []
        }

        const searchSuggestions = useDebounce(search, 1000)

        useOnOutsidePress(elementref, closeHeaderDropdown)
        useOnOutsidePress(searchref, resetSearchState)

        onBeforeMount(() => {
            if (!props.withoutSectors) {
                loadDispatcherSectors()
                new_dispatcher_store.initialize()
                new_dispatcher_store.validation$.$touch()
            }
        })

        const resetDispatcherSector = () => {
            new_dispatcher_store.dispatcherSectorIds = []
            new_dispatcher_store.dispatcherSectors = []
            new_dispatcher_store.syncWithLocalStorage()
        }

        const resetCustom = () => {
            new_dispatcher_store.isWithoutSectorId = false
            state.is_custom_checked = false
        }

        const onDispatcherSectorsChange = (dispatcher_sector_ids: number[]) => {
            resetCustom()

            if (!dispatcher_sector_ids.length) {
                resetDispatcherSector()
                return
            }

            const dispatcher_sectors = data_state.dispatcher_sectors.list.filter(
                (dispatcher_sector) => dispatcher_sector_ids.includes(dispatcher_sector.id)
            )

            if (!dispatcher_sectors.length) {
                resetDispatcherSector()
                return
            }

            new_dispatcher_store.dispatcherSectorIds = [...dispatcher_sector_ids]
            new_dispatcher_store.dispatcherSectors = [...dispatcher_sectors]

            new_dispatcher_store.syncWithLocalStorage()
        }

        const onSelectCustom = (value: number) => {
            resetDispatcherSector()

            new_dispatcher_store.isWithoutSectorId = Boolean(value)
            state.is_custom_checked = true
        }

        const isNullableOption = computed(() => {
            return new_dispatcher_store.WITH_NULLABLE_ROUTES.includes(route.path)
        })
        const isCustomOption = computed(() => {
            return new_dispatcher_store.WITH_CUSTOM_ROUTES.includes(route.path)
        })
        const isSectorsVisible = computed(() => {
            return !new_dispatcher_store.WITHOUT_SECTORS.includes(route.path)
        })

        return {
            searchref,
            elementref,
            new_dispatcher_store,
            state,
            search_state,
            data_state,
            searchSuggestions,
            toggleHeaderDropdown,
            loadDispatcherSectors,
            onDispatcherSectorsChange,
            isNullableOption,
            isCustomOption,
            isSectorsVisible,
            onSelectCustom,
        }
    },
})
